import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { getDeviceCatalog } from "../../lib/api";
import { PageLoader } from "../../components/ui/spinner";
import { Button } from "../../components/ui/button";

const PAGE_SIZE = 10;

const TABLE_HEADERS = [
  { key: "serialNumber", label: "SERIAL NO.", help: "Unique serial number assigned to the device." },
  { key: "bluetoothModule", label: "BLUETOOTH MODULE REFERENCE ID", help: "Bluetooth module reference linked to the device." },
  { key: "username", label: "USER NAME", help: "The name of the user assigned to this device." },
  { key: "country", label: "COUNTRY", help: "Country code where the device is registered." },
  { key: "allocationStatus", label: "ALLOCATION STATUS", help: "Current deployment state of the device." },
  { key: "factoryAllocatedTime", label: "FACTORY ALLOCATED TIME", help: "Time when the device was allocated at the factory." },
  { key: "firstFieldInUse", label: "FIRST FIELD IN USE", help: "First time the device was used in the field." },
  { key: "latestFieldInUse", label: "LATEST FIELD IN USE", help: "Most recent time the device was used in the field." },
  { key: "updatedAt", label: "UPDATED AT", help: "Last time the catalog entry was updated." },
];

const WORLD_COUNTRIES = [
  { name: "Afghanistan", code: "AF" },
  { name: "Albania", code: "AL" },
  { name: "Algeria", code: "DZ" },
  { name: "Andorra", code: "AD" },
  { name: "Angola", code: "AO" },
  { name: "Antigua and Barbuda", code: "AG" },
  { name: "Argentina", code: "AR" },
  { name: "Armenia", code: "AM" },
  { name: "Australia", code: "AU" },
  { name: "Austria", code: "AT" },
  { name: "Azerbaijan", code: "AZ" },
  { name: "Bahamas", code: "BS" },
  { name: "Bahrain", code: "BH" },
  { name: "Bangladesh", code: "BD" },
  { name: "Barbados", code: "BB" },
  { name: "Belarus", code: "BY" },
  { name: "Belgium", code: "BE" },
  { name: "Belize", code: "BZ" },
  { name: "Benin", code: "BJ" },
  { name: "Bhutan", code: "BT" },
  { name: "Bolivia", code: "BO" },
  { name: "Bosnia and Herzegovina", code: "BA" },
  { name: "Botswana", code: "BW" },
  { name: "Brazil", code: "BR" },
  { name: "Brunei", code: "BN" },
  { name: "Bulgaria", code: "BG" },
  { name: "Burkina Faso", code: "BF" },
  { name: "Burundi", code: "BI" },
  { name: "Cabo Verde", code: "CV" },
  { name: "Cambodia", code: "KH" },
  { name: "Cameroon", code: "CM" },
  { name: "Canada", code: "CA" },
  { name: "Central African Republic", code: "CF" },
  { name: "Chad", code: "TD" },
  { name: "Chile", code: "CL" },
  { name: "China", code: "CN" },
  { name: "Colombia", code: "CO" },
  { name: "Comoros", code: "KM" },
  { name: "Congo", code: "CG" },
  { name: "Costa Rica", code: "CR" },
  { name: "Croatia", code: "HR" },
  { name: "Cuba", code: "CU" },
  { name: "Cyprus", code: "CY" },
  { name: "Czech Republic", code: "CZ" },
  { name: "Denmark", code: "DK" },
  { name: "Djibouti", code: "DJ" },
  { name: "Dominica", code: "DM" },
  { name: "Dominican Republic", code: "DO" },
  { name: "Ecuador", code: "EC" },
  { name: "Egypt", code: "EG" },
  { name: "El Salvador", code: "SV" },
  { name: "Equatorial Guinea", code: "GQ" },
  { name: "Eritrea", code: "ER" },
  { name: "Estonia", code: "EE" },
  { name: "Eswatini", code: "SZ" },
  { name: "Ethiopia", code: "ET" },
  { name: "Fiji", code: "FJ" },
  { name: "Finland", code: "FI" },
  { name: "France", code: "FR" },
  { name: "Gabon", code: "GA" },
  { name: "Gambia", code: "GM" },
  { name: "Georgia", code: "GE" },
  { name: "Germany", code: "DE" },
  { name: "Ghana", code: "GH" },
  { name: "Greece", code: "GR" },
  { name: "Grenada", code: "GD" },
  { name: "Guatemala", code: "GT" },
  { name: "Guinea", code: "GN" },
  { name: "Guinea-Bissau", code: "GW" },
  { name: "Guyana", code: "GY" },
  { name: "Haiti", code: "HT" },
  { name: "Honduras", code: "HN" },
  { name: "Hungary", code: "HU" },
  { name: "Iceland", code: "IS" },
  { name: "India", code: "IN" },
  { name: "Indonesia", code: "ID" },
  { name: "Iran", code: "IR" },
  { name: "Iraq", code: "IQ" },
  { name: "Ireland", code: "IE" },
  { name: "Israel", code: "IL" },
  { name: "Italy", code: "IT" },
  { name: "Jamaica", code: "JM" },
  { name: "Japan", code: "JP" },
  { name: "Jordan", code: "JO" },
  { name: "Kazakhstan", code: "KZ" },
  { name: "Kenya", code: "KE" },
  { name: "Kiribati", code: "KI" },
  { name: "Kuwait", code: "KW" },
  { name: "Kyrgyzstan", code: "KG" },
  { name: "Laos", code: "LA" },
  { name: "Latvia", code: "LV" },
  { name: "Lebanon", code: "LB" },
  { name: "Lesotho", code: "LS" },
  { name: "Liberia", code: "LR" },
  { name: "Libya", code: "LY" },
  { name: "Liechtenstein", code: "LI" },
  { name: "Lithuania", code: "LT" },
  { name: "Luxembourg", code: "LU" },
  { name: "Madagascar", code: "MG" },
  { name: "Malawi", code: "MW" },
  { name: "Malaysia", code: "MY" },
  { name: "Maldives", code: "MV" },
  { name: "Mali", code: "ML" },
  { name: "Malta", code: "MT" },
  { name: "Marshall Islands", code: "MH" },
  { name: "Mauritania", code: "MR" },
  { name: "Mauritius", code: "MU" },
  { name: "Mexico", code: "MX" },
  { name: "Micronesia", code: "FM" },
  { name: "Moldova", code: "MD" },
  { name: "Monaco", code: "MC" },
  { name: "Mongolia", code: "MN" },
  { name: "Montenegro", code: "ME" },
  { name: "Morocco", code: "MA" },
  { name: "Mozambique", code: "MZ" },
  { name: "Myanmar", code: "MM" },
  { name: "Namibia", code: "NA" },
  { name: "Nauru", code: "NR" },
  { name: "Nepal", code: "NP" },
  { name: "Netherlands", code: "NL" },
  { name: "New Zealand", code: "NZ" },
  { name: "Nicaragua", code: "NI" },
  { name: "Niger", code: "NE" },
  { name: "Nigeria", code: "NG" },
  { name: "North Korea", code: "KP" },
  { name: "North Macedonia", code: "MK" },
  { name: "Norway", code: "NO" },
  { name: "Oman", code: "OM" },
  { name: "Pakistan", code: "PK" },
  { name: "Palau", code: "PW" },
  { name: "Palestine", code: "PS" },
  { name: "Panama", code: "PA" },
  { name: "Papua New Guinea", code: "PG" },
  { name: "Paraguay", code: "PY" },
  { name: "Peru", code: "PE" },
  { name: "Philippines", code: "PH" },
  { name: "Poland", code: "PL" },
  { name: "Portugal", code: "PT" },
  { name: "Qatar", code: "QA" },
  { name: "Romania", code: "RO" },
  { name: "Russia", code: "RU" },
  { name: "Rwanda", code: "RW" },
  { name: "Saint Kitts and Nevis", code: "KN" },
  { name: "Saint Lucia", code: "LC" },
  { name: "Saint Vincent and the Grenadines", code: "VC" },
  { name: "Samoa", code: "WS" },
  { name: "San Marino", code: "SM" },
  { name: "Sao Tome and Principe", code: "ST" },
  { name: "Saudi Arabia", code: "SA" },
  { name: "Senegal", code: "SN" },
  { name: "Serbia", code: "RS" },
  { name: "Seychelles", code: "SC" },
  { name: "Sierra Leone", code: "SL" },
  { name: "Singapore", code: "SG" },
  { name: "Slovakia", code: "SK" },
  { name: "Slovenia", code: "SI" },
  { name: "Solomon Islands", code: "SB" },
  { name: "Somalia", code: "SO" },
  { name: "South Africa", code: "ZA" },
  { name: "South Korea", code: "KR" },
  { name: "South Sudan", code: "SS" },
  { name: "Spain", code: "ES" },
  { name: "Sri Lanka", code: "LK" },
  { name: "Sudan", code: "SD" },
  { name: "Suriname", code: "SR" },
  { name: "Sweden", code: "SE" },
  { name: "Switzerland", code: "CH" },
  { name: "Syria", code: "SY" },
  { name: "Taiwan", code: "TW" },
  { name: "Tajikistan", code: "TJ" },
  { name: "Tanzania", code: "TZ" },
  { name: "Thailand", code: "TH" },
  { name: "Timor-Leste", code: "TL" },
  { name: "Togo", code: "TG" },
  { name: "Tonga", code: "TO" },
  { name: "Trinidad and Tobago", code: "TT" },
  { name: "Tunisia", code: "TN" },
  { name: "Turkey", code: "TR" },
  { name: "Turkmenistan", code: "TM" },
  { name: "Tuvalu", code: "TV" },
  { name: "Uganda", code: "UG" },
  { name: "Ukraine", code: "UA" },
  { name: "United Arab Emirates", code: "AE" },
  { name: "United Kingdom", code: "GB" },
  { name: "United States", code: "US" },
  { name: "Uruguay", code: "UY" },
  { name: "Uzbekistan", code: "UZ" },
  { name: "Vanuatu", code: "VU" },
  { name: "Vatican City", code: "VA" },
  { name: "Venezuela", code: "VE" },
  { name: "Vietnam", code: "VN" },
  { name: "Yemen", code: "YE" },
  { name: "Zambia", code: "ZM" },
  { name: "Zimbabwe", code: "ZW" },
];

// Pill colour per allocation status. A lookup rather than a ternary chain so the
// default case stays obvious (S3358).
const ALLOCATION_STATUS_COLORS = {
  "Field": "bg-blue-100 text-blue-800",
  "Factory": "bg-amber-100 text-amber-800",
  "Yet to be Assigned": "bg-pink-100 text-pink-800",
};

function allocationStatusColor(label) {
  return ALLOCATION_STATUS_COLORS[label] || "bg-slate-100 text-slate-800";
}

// Function to determine allocation status from API status, fieldInUse, and active registration filter
function getAllocationStatus(apiStatus, fieldInUse, activeFilter) {
  if (activeFilter === "field") return "Field";
  if (activeFilter === "factory") return "Factory";
  if (!apiStatus || apiStatus === "—") return "—";
  const statusLower = String(apiStatus).toLowerCase();
  if (fieldInUse === "yes" || fieldInUse === true) return "Field";
  if (statusLower.includes("unallocated")) return "Yet to be Assigned";
  if (statusLower.includes("allocated")) return "Factory";
  return apiStatus;
}

// Function to format timestamp to date time only
function formatTimestamp(timestamp) {
  if (!timestamp) return "—";
  // Format: "2026-06-19 08:16:50" from "2026-06-19T08:16:50.458635+00:00"
  const [date, time] = timestamp.split('T');
  const timeOnly = time?.split('.')[0] || time;
  return `${date} ${timeOnly}`;
}

export default function DeviceCatalogPage() {
  const [search, setSearch]           = useState("");
  const [devices, setDevices]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [pagination, setPagination]   = useState({ page: 1, totalPages: 1, totalRecords: 0 });
  const [latestUpdatedAt, setLatestUpdatedAt] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [activeHelpKey, setActiveHelpKey] = useState(null);
  const helpTriggerRefs = useRef({});

  useEffect(() => {
    function handlePointerDown(event) {
      if (!activeHelpKey) return;
      const activeTrigger = helpTriggerRefs.current[activeHelpKey];
      if (activeTrigger && !activeTrigger.contains(event.target)) {
        setActiveHelpKey(null);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setActiveHelpKey(null);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [activeHelpKey]);

  const fetchPage = useCallback(async (page = 1, searchTerm = search, status = statusFilter, country = countryFilter) => {
    setLoading(true);
    try {
      const q = searchTerm.trim().toLowerCase();

      if (q) {
        // Search across all devices (fetch all pages), then paginate the filtered set.
        let allDevices = [];
        let currentPage = 1;
        let totalPages = 1;

        do {
          const data = await getDeviceCatalog({
            registrationFilter: status || "all",
            page: currentPage,
            limit: PAGE_SIZE,
            country: country || undefined,
          });
          allDevices = allDevices.concat(data.devices || []);
          totalPages = Number(data.pagination?.totalPages ?? totalPages);
          currentPage += 1;
        } while (currentPage <= totalPages);

        const filteredDevices = allDevices.filter((d) => 
          d.serialNumber?.toLowerCase().includes(q) ||
          d.username?.toLowerCase().includes(q)
        );
        const totalRecords = filteredDevices.length;
        const filteredTotalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));
        const safePage = Math.min(Math.max(page, 1), filteredTotalPages);
        const start = (safePage - 1) * PAGE_SIZE;

        setDevices(filteredDevices.slice(start, start + PAGE_SIZE));
        setPagination({
          page: safePage,
          totalPages: filteredTotalPages,
          totalRecords,
        });
      } else {
        const data = await getDeviceCatalog({
          registrationFilter: status || "all",
          page,
          limit: PAGE_SIZE,
          country: country || undefined,
        });
        const devs = data.devices || [];
        const pag = data.pagination || {};

        setDevices(devs);
        setPagination({
          page:         pag.page         ?? page,
          totalPages:   pag.totalPages   ?? 1,
          totalRecords: pag.totalRecords ?? devs.length,
        });
        setLatestUpdatedAt(data.latestUpdatedAt || null);
      }
    } catch {
      setDevices([]);
      setPagination({ page: 1, totalPages: 1, totalRecords: 0 });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, countryFilter]);

  useEffect(() => {
    fetchPage(1, search, statusFilter, countryFilter);
  }, [search, statusFilter, countryFilter, fetchPage]);


  const isInitialLoading = loading && devices.length === 0;
  if (isInitialLoading) return <PageLoader />;

  const renderHeaderHelp = (header) => (
    <span
      ref={(node) => {
        if (node) {
          helpTriggerRefs.current[header.key] = node;
        } else {
          delete helpTriggerRefs.current[header.key];
        }
      }}
      className="relative inline-flex items-center"
    >
      <button
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[#08148C] hover:text-[#06106b] focus:outline-none focus:ring-2 focus:ring-[#08148C]/30"
        onClick={() => setActiveHelpKey((current) => (current === header.key ? null : header.key))}
        aria-label={`Show help for ${header.label}`}
        aria-expanded={activeHelpKey === header.key}
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {activeHelpKey === header.key && (
        <div
          className={`absolute top-full z-20 mt-2 w-64 rounded-lg border border-slate-200 bg-white p-3 text-left normal-case shadow-lg max-w-[calc(100vw-1.5rem)] ${
            header.key === "serialNumber" ? "left-0" : "left-1/2 -translate-x-1/2"
          }`}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-[#08148C]">{header.label}</p>
          <p className="mt-1 text-sm leading-snug text-slate-600">{header.help}</p>
        </div>
      )}
    </span>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Device Catalog</h1>
          <p className="text-sm text-slate-500 mt-0.5">Registered devices across all deployment states.</p>
        </div>
        {latestUpdatedAt && (
          <p className="text-sm font-semibold text-slate-600 text-right">Latest updated: {formatTimestamp(latestUpdatedAt)}</p>
        )}
      </div>

      {/* Main card */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">

        {/* Filter row */}
        <div className="flex flex-wrap items-end gap-4 px-5 py-4 border-b border-slate-100">
          {/* Status + Country inline */}
          <div className="flex items-center gap-3">
            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none text-sm border border-slate-300 rounded-lg px-4 py-2.5 bg-white text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-[#08148C] focus:border-transparent cursor-pointer hover:border-slate-400 transition-colors"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  paddingRight: '2rem',
                }}
              >
                <option value="">All Status</option>
                <option value="field">Field</option>
                <option value="factory">Factory</option>
              </select>
            </div>

            {/* Country Filter */}
            <div>
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="appearance-none text-sm border border-slate-300 rounded-lg px-4 py-2.5 bg-white text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-[#08148C] focus:border-transparent cursor-pointer hover:border-slate-400 transition-colors"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  paddingRight: '2rem',
                }}
              >
                <option value="">All Countries</option>
                {WORLD_COUNTRIES.map(({ name, code }) => (
                  <option key={code} value={code}>
                    {name} ({code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Search */}
          <div className="relative ml-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#08148C]/20 w-56"
              placeholder="Search serial no..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <span className="text-xs font-bold text-slate-500 whitespace-nowrap">
            {pagination.totalRecords.toLocaleString()} devices
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                {TABLE_HEADERS.map(({ key, label, help }) => (
                  <th key={key} className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400 align-middle">
                    <span className="inline-flex items-center gap-1.5">
                      <span>{label}</span>
                      {renderHeaderHelp({ key, label, help })}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loading && (
                <tr>
                  <td colSpan={9} className="py-12 text-center">
                    <PageLoader />
                  </td>
                </tr>
              )}
              {!loading && devices.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-slate-400 py-12">No devices found.</td>
                </tr>
              )}
              {!loading && devices.map((d) => (
                <tr key={d.deviceId || d.serialNumber} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-4 font-mono text-sm text-slate-700 whitespace-nowrap">{d.serialNumber || "—"}</td>
                  <td className="px-5 py-4 text-sm text-slate-700 font-mono">{d.macId || "—"}</td>
                  <td className="px-5 py-4 text-sm text-slate-700 font-medium">{d.username || "—"}</td>
                  <td className="px-5 py-4 text-sm text-slate-700">{d.country || "—"}</td>
                  <td className="px-5 py-4 text-sm">
                    {(() => {
                      const label = getAllocationStatus(d.status, d.fieldInUse, statusFilter);
                      const colorClass = allocationStatusColor(label);
                      return (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600">{formatTimestamp(d.factoryAllocatedTime) || "—"}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{formatTimestamp(d.firstFieldInUse) || "—"}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{formatTimestamp(d.latestFieldInUse) || "—"}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{formatTimestamp(d.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {devices.length > 0 && (
        <div className="flex justify-center">
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || pagination.page <= 1}
              onClick={() => fetchPage(pagination.page - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Prev
            </Button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => {
              const current = pagination.page;
              const total   = pagination.totalPages;
              const show    = p === 1 || p === total || Math.abs(p - current) <= 1;
              const ellipsisBefore = p === current - 2 && current - 2 > 1;
              const ellipsisAfter  = p === current + 2 && current + 2 < total;
              if (ellipsisBefore || ellipsisAfter) return <span key={p} className="px-1 text-sm text-slate-400">...</span>;
              if (!show) return null;
              return (
                <Button
                  key={p}
                  type="button"
                  variant={p === current ? "default" : "outline"}
                  size="sm"
                  disabled={loading}
                  onClick={() => fetchPage(p)}
                  className="min-w-9"
                >
                  {p}
                </Button>
              );
            })}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading || pagination.page >= pagination.totalPages}
              onClick={() => fetchPage(pagination.page + 1)}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
